/**
 * Card pricing bridge - single entry point used by BulkScanner, FairTrade and
 * the card price cache.
 *
 * HISTORY: this used to call the `countdown-pricing` edge function. As of
 * August 2026 eBay requires a signed-in session to view sold listings, so
 * Countdown dropped the `sold_items` parameter that function depends on. The
 * endpoint now returns HTTP 400 and the edge function answers 200 with zero
 * listings - a silent failure. See docs/2026-08-17/PRICING-AUDIT.md.
 *
 * Pricing now comes from Poketrace, which maintains its own eBay sales database
 * rather than scraping eBay search, and is already the app's pricing engine.
 * The return shapes below are unchanged so callers did not need touching.
 */

import {
  getAllPriceTiers, gradeToPoketraceKey, formatTierLabel, fetchPoketraceListings,
} from '../utils/poketraceService';

// Poketrace raw tier key -> the short keys FairTrade expects
const RAW_KEY_MAP = {
  NEAR_MINT: 'nm',
  LIGHTLY_PLAYED: 'lp',
  MODERATELY_PLAYED: 'mp',
  HEAVILY_PLAYED: 'hp',
  DAMAGED: 'dmg',
};

// A tier's headline number: prefer the 30-day median, fall back to the average.
const tierPrice = (t) => (t?.median30d ?? t?.avg ?? null);

/**
 * Build the `stats` object callers persist to the cache and render from.
 * Keyed by human label ("Near Mint", "PSA 10") to match what countdown-pricing
 * produced, so existing readers keep working.
 */
function toStats(tiers) {
  const stats = {};
  for (const [tier, data] of Object.entries(tiers || {})) {
    const { company, grade } = formatTierLabel(tier);
    const label = company ? `${company} ${grade}` : grade;
    stats[label] = {
      med: tierPrice(data),
      avg: data?.avg ?? null,
      low: data?.low ?? null,
      high: data?.high ?? null,
      count: data?.saleCount ?? 0,
      lastUpdated: data?.lastUpdated ?? null,
    };
  }
  return stats;
}

const countSales = (tiers) =>
  Object.values(tiers || {}).reduce((n, t) => n + (t?.saleCount || 0), 0);

/**
 * The individual sales behind the aggregates.
 *
 * This used to return nothing, on the belief that Poketrace only publishes
 * aggregates. It publishes both: `/cards/{id}/listings` hands back every sold
 * listing with its title, its eBay link, the grading company and grade the
 * seller declared, and Poketrace's own anomaly verdict on the price.
 *
 * That last field is why this matters beyond letting someone tap a number.
 * Roughly one sale in twenty is flagged, and a flagged sale inside a three-sale
 * average is a third of the answer.
 *
 * Never throws. A card that prices but cannot list its sales should still show
 * its price.
 */
async function fetchEvidence(cardId) {
  if (!cardId) return [];
  try {
    const listings = await fetchPoketraceListings(cardId);
    return (listings || [])
      .filter((l) => l?.price > 0)
      .map((l) => ({
        // Kept separate rather than folded into one condition string. The old
        // cache put "PSA 9" in `condition` and lost the distinction between a
        // grade and a raw quality, which the reader then had to guess back.
        company: l.grader ? String(l.grader).toLowerCase() : 'raw',
        grade_number: l.grade != null ? (parseFloat(l.grade) || l.grade) : null,
        condition: l.condition || null,
        price: l.price,
        title: l.title || null,
        url: l.listingUrl || null,
        date: l.soldAt ? String(l.soldAt).split('T')[0] : null,
        anomalyFlag: l.anomalyFlag || null,
        anomalyReason: l.anomalyReason || null,
        source: 'ebay',
      }));
  } catch (err) {
    console.warn('[Pricing] listings unavailable:', err?.message);
    return [];
  }
}

/**
 * Raw (ungraded) pricing.
 *
 * @returns {{ evidence, stats, conditions, searchQuery, totalListings, source } | null}
 */
export async function fetchPricing({
  name, setName, localId, variant, specification, reportCard, onFailure, tiers: given,
}) {
  if (!name) return null;

  try {
    // The caller usually already has these. Card detail asks for the ladder and
    // for the individual sales at the same time, and both used to resolve the
    // card at the vendor independently: two searches and two pricing fetches
    // for one card, in sequence within their own pipelines. On a card the
    // vendor had never been asked about that was most of the thirty seconds.
    const tiers = given || await getAllPriceTiers({
      cardName: name,
      cardNumber: localId,
      setName,
      variant,
      reportCard,
      onFailure,
    });
    if (!tiers) return null;

    const raw = tiers.raw || {};

    // Short-key view for callers that ask for a specific condition.
    const conditions = {};
    for (const [tier, data] of Object.entries(raw)) {
      const key = RAW_KEY_MAP[tier];
      if (!key) continue;
      conditions[key] = {
        price: tierPrice(data),
        low: data?.low ?? null,
        high: data?.high ?? null,
        saleCount: data?.saleCount ?? 0,
      };
    }

    return {
      evidence: await fetchEvidence(tiers.cardId),
      stats: toStats(raw),
      conditions,
      searchQuery: [name, localId, setName].filter(Boolean).join(' '),
      totalListings: countSales(raw),
      tcgplayer_product_id: tiers.tcgplayerId || null,
      source: 'poketrace',
    };
  } catch (err) {
    console.error('[Pricing] raw error:', err.message);
    return null;
  }
}

/**
 * Graded pricing for one slab, e.g. condition "PSA 10" or "CGC 9.5".
 *
 * @returns {{ price, pricingData }}
 */
export async function fetchGradedPricing({ name, setName, number, condition, variant, reportCard, onFailure }) {
  if (!name || !condition) return { price: null, pricingData: null };

  const company = (condition.trim().split(/\s+/)[0] || 'PSA').toUpperCase();

  try {
    const tiers = await getAllPriceTiers({
      cardName: name,
      cardNumber: number,
      setName,
      variant,
      reportCard,
      onFailure,
      // A slab sold somewhere. That somewhere is eBay.
      gradedOnly: true,
    });
    if (!tiers) return { price: null, pricingData: null };

    const graded = tiers.graded || {};
    const key = gradeToPoketraceKey(condition);
    const price = tierPrice(graded[key]);

    return {
      price,
      pricingData: {
        isGraded: true,
        company,
        evidence: await fetchEvidence(tiers.cardId),
        stats: toStats(graded),
        searchQuery: [condition, name, number, setName].filter(Boolean).join(' '),
        totalListings: graded[key]?.saleCount || 0,
        source: 'poketrace',
      },
    };
  } catch (err) {
    console.error('[Pricing] graded error:', err.message);
    return { price: null, pricingData: null };
  }
}
